// Convert a list of media URLs (typically QR-uploaded "dirt" photos that live
// in /media) into File objects for a multipart upload.
//
// The backend validates uploads on BOTH the file extension AND the content
// type. Media is now stored as optimized .webp, so each File must carry its
// real MIME type and a matching extension. The previous inline implementation
// hard-coded `image{n}.jpg` + `image/jpeg` for every file, which mislabels the
// WebP bytes and is fragile against stricter upload validation. Here we read
// the blob's real type and derive the extension from it.
//
// URLs that fail to download are skipped (Promise.allSettled) so a single
// unreachable photo can never reject the whole order / feedback submission.

const extensionForMime = (mime) => {
    const subtype = (mime.split('/')[1] || '').toLowerCase();
    if (subtype === 'jpeg') return 'jpg';
    return subtype || 'jpg';
};

// Media serving can flap (a saturated origin returns 502 or, worse, a 200 with
// an EMPTY body). An empty/partial blob still has res.ok === true, so without a
// size check it became a 0-byte File that the backend ImageField rejected
// ("Upload a valid image ... corrupted image") and 400'd the WHOLE order. We
// retry transient failures a few times and, if a photo still can't be fetched
// as a non-empty image, we DROP it (Promise.allSettled) rather than poison the
// submission — a missing dirt photo must never block order creation.
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 400;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const urlsToFiles = async (photoUrls = []) => {
    const toFile = async (url, index) => {
        let lastError;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
            try {
                const res = await fetch(url, { cache: 'no-store' });
                if (!res.ok) {
                    throw new Error(`Failed to download photo: ${res.status}`);
                }
                const blob = await res.blob();
                // Guard against empty/partial responses: a 0-byte blob is not a
                // valid image and would 400 the order on upload.
                if (!blob || blob.size === 0) {
                    throw new Error('Downloaded photo is empty');
                }
                const type = blob.type && blob.type.startsWith('image/')
                    ? blob.type
                    : 'image/jpeg';
                return new File([blob], `image${index}.${extensionForMime(type)}`, { type });
            } catch (error) {
                lastError = error;
                if (attempt < MAX_ATTEMPTS) {
                    await delay(RETRY_DELAY_MS * attempt);
                }
            }
        }
        throw lastError;
    };

    const results = await Promise.allSettled(
        photoUrls.map((url, index) => toFile(url, index))
    );

    return results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);
};
